import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "../lib/notify";
import { Save, Pencil, Search, X } from "lucide-react";
import { http, formatApiError } from "../lib/api";
import { fmtDate } from "../lib/format";
import SearchableSelect from "./SearchableSelect";
import { useServiceRows, persistRows, emptyEntry } from "./branch/useServiceRows";
import { CategoryTabs } from "./branch/CategoryTabs";
import EntryRow from "./branch/EntryRow";

const norm = (s) => (s || "").toLocaleUpperCase("tr");
const isAsil = (c) => norm(c.name).includes("ASIL PERSONEL");
const isGecici = (c) => norm(c.name).includes("GEÇİCİ PERSONEL") || norm(c.name).includes("GECICI PERSONEL");

const LOC_PUT_KEYS = ["project_id", "region_id", "subcontractor_id", "approver_user_id", "name", "branch_code", "city", "district", "branch_type", "branch_m2", "area_m2", "floor_count_total", "floor_count_used", "latitude", "longitude", "monthly_wipe_count", "sgk_no", "sgk_agent_no", "ebildirge_user1", "ebildirge_user2", "ebildirge_sys_pass", "ebildirge_work_pass", "unit"];
const locPutBody = (loc) => Object.fromEntries(LOC_PUT_KEYS.filter((k) => loc[k] !== undefined && loc[k] !== null).map((k) => [k, loc[k]]));

export default function BranchServices({ loc, branchSubIds = [], onSubsChange, projectId, categories, services, allCategories, allServices, onCategoryAssigned, caris, userLabels = {}, initialCat }) {
  const saveSubs = async (ids) => {
    const body = locPutBody(loc);
    try { await http.put(`/locations/${loc.id}`, { ...body, subcontractor_ids: ids }); toast.success("Şube taşeronları güncellendi"); onSubsChange?.(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const navigate = useNavigate();
  const [open, setOpen] = useState(initialCat || null);
  const [asgs, setAsgs] = useState([]);
  const [people, setPeople] = useState([]);
  const [added, setAdded] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [focusSvc, setFocusSvc] = useState(null); // Hizmet Ekle'den seçilen alt hizmet → satırı vurgula, düzenlemeyi aç

  const loadAsgs = useCallback(() => http.get("/assignments", { params: { location_id: loc.id } }).then((r) => setAsgs(r.data)).catch(() => {}), [loc.id]);
  useEffect(() => {
    if (!addOpen) return;
    const onKey = (e) => { if (e.key === "Escape") { e.stopPropagation(); setAddOpen(false); } };
    const onClick = (e) => { if (!e.target.closest("[data-testid=bs-add-menu], [data-testid=bs-add-service]")) setAddOpen(false); };
    window.addEventListener("keydown", onKey, true); document.addEventListener("mousedown", onClick);
    return () => { window.removeEventListener("keydown", onKey, true); document.removeEventListener("mousedown", onClick); };
  }, [addOpen]);
  useEffect(() => {
    loadAsgs();
    http.get("/personnel", { params: { location_id: loc.id } }).then((r) => setPeople(r.data)).catch(() => {});
  }, [loc.id, loadAsgs]);

  const asilCat = categories.find(isAsil) || { id: "__asil", name: "ASIL PERSONEL" };
  const geciciCat = categories.find(isGecici) || { id: "__gecici", name: "GEÇİCİ PERSONEL" };
  const { otherCats } = useMemo(() => {
    const svcCatIds = new Set(asgs.map((a) => services.find((s) => s.id === a.service_id)?.category_id).filter(Boolean));
    const rest = categories.filter((c) => !isAsil(c) && !isGecici(c));
    return { otherCats: rest.filter((c) => svcCatIds.has(c.id) || added.includes(c.id)) };
  }, [categories, asgs, services, added]);
  const tabs = [asilCat, geciciCat, ...otherCats];
  const openCat = otherCats.find((c) => c.id === open);
  // Hizmet Ekle menüsü: Hizmetler kataloğunun TÜM aktif alt hizmetleri (personel başlıkları hariç); projede henüz açık olmayan başlıklar işaretli
  const svcOpts = useMemo(() => {
    const cats = allCategories || categories;
    const svcs = allServices || services;
    const inProject = new Set(categories.map((c) => c.id));
    const catName = Object.fromEntries(cats.filter((c) => c.status !== "Pasif" && !isAsil(c) && !isGecici(c)).map((c) => [c.id, c.name]));
    const svcVisible = (s) => (!s.project_id || s.project_id === projectId) && (s.scope !== "projects" || (s.project_ids || []).includes(projectId));
    return svcs.filter((s) => s.status !== "Pasif" && catName[s.category_id] && svcVisible(s))
      .map((s) => ({ ...s, category_name: catName[s.category_id], in_project: inProject.has(s.category_id), assigned: asgs.some((a) => a.service_id === s.id) }))
      .sort((a, b) => a.category_name.localeCompare(b.category_name, "tr") || (a.name || "").localeCompare(b.name || "", "tr"));
  }, [services, categories, allServices, allCategories, projectId, asgs]);
  const addService = async (s) => {
    if (!s.in_project) { // başlık bu projede açık değil → projeye ekle (ana hizmeti şube kartından açma)
      try {
        await http.post(`/categories/${s.category_id}/scope`, { project_id: projectId, action: "assign" });
        await onCategoryAssigned?.();
        toast.success(`"${s.category_name}" başlığı projeye eklendi`);
      } catch (err) { toast.error(formatApiError(err)); return; }
    }
    if (!otherCats.some((c) => c.id === s.category_id)) setAdded((a) => [...a, s.category_id]);
    setOpen(s.category_id); setFocusSvc(s.id); setAddOpen(false);
  };

  const openPersonnel = (p, catId) => {
    const back = `/tanimlar/projeler?proje=${projectId}&sube=${loc.id}&hizmet=${catId}`;
    navigate(`/veri-girisi?bolum=personel&personel=${p.id}&back=${encodeURIComponent(back)}`);
  };

  const openServices = useMemo(() => services.filter((s) => s.category_id === open), [services, open]);
  return (
    <div className="mt-3 border-2 border-slate-300 rounded-xl p-3 bg-white" data-testid={`branch-services-${loc.id}`}>
      <div className="text-sm font-extrabold tracking-wide text-slate-800 mb-2">HİZMETLER</div>
      <CategoryTabs tabs={tabs} open={open} onToggle={(id) => setOpen(open === id ? null : id)} services={svcOpts} addOpen={addOpen} setAddOpen={setAddOpen} onAddService={addService} />
      {open === asilCat.id && <PersonnelList people={people.filter((p) => p.position !== "Geçici")} onOpen={(p) => openPersonnel(p, asilCat.id)} mode="asil" />}
      {open === geciciCat.id && <PersonnelList people={people.filter((p) => p.position === "Geçici")} onOpen={(p) => openPersonnel(p, geciciCat.id)} mode="gecici" userLabels={userLabels} />}
      {openCat && <ServiceRows key={openCat.id} cat={openCat} loc={loc} branchSubIds={branchSubIds} saveSubs={saveSubs} services={openServices} asgs={asgs} caris={caris} reload={loadAsgs} focusSvc={focusSvc} />}
    </div>
  );
}

function PersonnelList({ people, onOpen, mode, userLabels = {} }) {
  const [f, setF] = useState({ name: "", tc: "", title: "" });
  const rows = people.filter((p) => norm(p.full_name).includes(norm(f.name)) && (p.tc_no || "").includes(f.tc.trim()) && norm(p.title).includes(norm(f.title)));
  const who = (by) => (by && (userLabels[by]?.name || by)) || "—";
  return (
    <div className="mt-3 border border-slate-200 rounded-md overflow-hidden" data-testid={`bs-personnel-${mode}`}>
      <div className="flex flex-wrap gap-2 p-2 bg-slate-50 border-b border-slate-200">
        {[["name", "Adı Soyadı"], ["tc", "TC No"], ["title", "Görev"]].map(([k, ph]) => (
          <div key={k} className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} placeholder={ph} data-testid={`bs-pf-${k}`} className="pl-7 pr-6 py-1 text-xs border border-slate-300 rounded-md bg-white w-44 focus:border-amber-500 outline-none" />
            {f[k] && <button type="button" onClick={() => setF({ ...f, [k]: "" })} className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"><X className="w-3 h-3" /></button>}
          </div>
        ))}
        <span className="ml-auto text-[11px] text-slate-500 self-center">{rows.length} personel</span>
      </div>
      <table className="w-full">
        <thead className="bg-slate-100">
          <tr>
            {(mode === "asil" ? ["Adı Soyadı", "TC No", "Görev", "Durum"] : ["Adı Soyadı", "TC No", "Görev", "İşe Başlama", "Bitiş", "Görevlendiren"]).map((h) => (
              <th key={h} className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-slate-600 font-semibold text-left">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={6} className="px-3 py-3 text-xs text-slate-400 text-center">Bu şubede kayıtlı personel yok</td></tr>}
          {rows.map((p) => (
            <tr key={p.id} className="border-t border-slate-100 hover:bg-orange-50" data-testid={`bs-person-${p.id}`}>
              <td className="px-3 py-1.5 text-xs font-semibold"><button type="button" onClick={() => onOpen(p)} data-testid={`bs-person-open-${p.id}`} className="text-left hover:text-orange-700 hover:underline">{p.full_name}</button></td>
              <td className="px-3 py-1.5 text-xs font-mono">{p.tc_no || "—"}</td>
              <td className="px-3 py-1.5 text-xs">{p.title || "—"}</td>
              {mode === "asil" ? (
                <td className="px-3 py-1.5 text-xs">{p.status}</td>
              ) : (
                <>
                  <td className="px-3 py-1.5 text-xs">{fmtDate(p.hire_date) || "—"}</td>
                  <td className="px-3 py-1.5 text-xs">{fmtDate(p.leave_date) || "—"}</td>
                  <td className="px-3 py-1.5 text-xs">{who(p.created_by)}</td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RowsHeader({ cat, count, editing, saving, onSave, onEdit, onCancel }) {
  return (
    <div className="flex items-center gap-3 px-3 py-2 bg-slate-900 text-white rounded-t-md" {...(editing ? { "data-esc-trap": "1" } : {})}>
      <span className="text-xs font-extrabold uppercase">{cat.name}</span>
      <span className="text-[11px] text-slate-300">{count} alt hizmet</span>
      <div className="ml-auto flex items-center gap-2">
        {editing ? (
          <>
            <button type="button" onClick={onCancel} disabled={saving} data-testid={`bs-cancel-${cat.id}`} title="Kaydedilmemiş değişiklikleri at (Esc)" className="inline-flex items-center gap-1 px-3 py-1 rounded-md border border-slate-500 text-slate-200 text-xs font-semibold hover:bg-slate-700 hover:text-white disabled:opacity-60"><X className="w-3.5 h-3.5" /> İptal</button>
            <button type="button" onClick={onSave} disabled={saving} data-testid={`bs-save-${cat.id}`} className="inline-flex items-center gap-1 px-3 py-1 rounded-md bg-orange-500 text-white text-xs font-semibold hover:bg-orange-600 disabled:opacity-60"><Save className="w-3.5 h-3.5" /> {saving ? "Kaydediliyor…" : "Kaydet"}</button>
          </>
        ) : (
          <button type="button" onClick={onEdit} data-testid={`bs-edit-${cat.id}`} className="inline-flex items-center gap-1 px-3 py-1 rounded-md bg-white text-slate-900 text-xs font-semibold hover:bg-slate-200"><Pencil className="w-3.5 h-3.5" /> Düzenle</button>
        )}
      </div>
    </div>
  );
}

function SubChips({ cat, chipIds, filterSub, setFilterSub, editing, branchSubIds, saveSubs, subLabel, addSubOpts }) {
  return (
    <div className="px-3 py-2 border-b border-slate-200 bg-white" data-testid={`bs-subs-bar-${cat.id}`}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Taşeron(lar) <span className="normal-case tracking-normal font-normal">— birine tıklayın: yalnız o taşeronun fiyatları</span></div>
      <div className="flex flex-wrap items-center gap-2">
        {chipIds.length === 0 && !editing && <span className="text-xs text-slate-400">Bu şubede taşeron seçilmedi — Düzenle ile ekleyin</span>}
        {chipIds.map((id) => (
          <span key={id} className={`inline-flex items-center gap-1 rounded-md border-2 px-3 py-1.5 text-xs font-bold ${filterSub === id ? "bg-slate-900 text-white border-slate-900" : "border-slate-800 text-slate-900 hover:bg-slate-100"}`}>
            <button type="button" onClick={() => setFilterSub(filterSub === id ? "" : id)} data-testid={`bs-sub-chip-${id}`}>{subLabel(id)}</button>
            {editing && branchSubIds.includes(id) && <button type="button" onClick={() => { if (filterSub === id) setFilterSub(""); saveSubs(branchSubIds.filter((x) => x !== id)); }} title="Şubeden kaldır" className="ml-1 opacity-60 hover:opacity-100" data-testid={`bs-sub-chip-remove-${id}`}><X className="w-3 h-3" /></button>}
          </span>
        ))}
        {editing && <div className="w-64"><SearchableSelect testid={`bs-sub-add-${cat.id}`} value="" onChange={(v) => { if (v && !branchSubIds.includes(v)) saveSubs([...branchSubIds, v]); }} placeholder="+ Taşeron ekle…" options={addSubOpts} /></div>}
        {filterSub && <button type="button" onClick={() => setFilterSub("")} className="text-[11px] text-slate-500 hover:text-slate-900 underline" data-testid="bs-sub-filter-clear">Tüm taşeronlar</button>}
      </div>
    </div>
  );
}

function ServiceTableBody({ sorted, rows, cardsOf, filterSub, editing, subOpts, pickSub, setEntry, applyToAll, addEntry, remove, focusSvc }) {
  if (sorted.length === 0) return <tbody><tr><td colSpan={6} className="px-3 py-3 text-xs text-slate-400 text-center">Bu başlıkta projeye tanımlı alt hizmet yok</td></tr></tbody>;
  return (
    <tbody>
      {sorted.map((s) => {
        const fullList = rows[s.id] || [emptyEntry()]; const cs = cardsOf(s.id);
        let list = filterSub ? fullList.filter((e) => e.subcontractor_id === filterSub) : fullList;
        if (filterSub && list.length === 0) list = [{ ...emptyEntry(), key: `${s.id}-${filterSub}`, subcontractor_id: filterSub, _ghost: true }];
        const subCount = editing ? fullList.filter((e) => e.subcontractor_id).length : cs.length;
        return list.map((e, idx) => {
          const c = cs.find((x) => x.id === e.id);
          return <EntryRow key={e.key} s={s} e={e} c={c} idx={idx} isLast={idx === list.length - 1} subCount={subCount} canRemove={!!(c || e.subcontractor_id || e.buy_price || list.length > 1)} highlight={s.id === focusSvc}
            editing={editing} subOpts={subOpts} pickSub={pickSub} setEntry={setEntry} applyToAll={applyToAll} addEntry={addEntry} remove={remove} />;
        });
      })}
    </tbody>
  );
}

const TABLE_HEADERS = ["Alt Hizmet Adı", "Taşeron", "Taşeron Fiyatı", "Fiyat Geçerlilik Tarihi", "Görev Sonlandırma Tarihi", ""];
const HEADER_HINT = { "Fiyat Geçerlilik Tarihi": "Düzenlemede tarihin yanındaki ✓✓ düğmesi: satırdaki taşeronu ve geçerlilik tarihini tüm alt hizmetlere uygular" };

function ServiceRows({ cat, loc, branchSubIds = [], saveSubs, services, asgs, caris, reload, focusSvc }) {
  const [filterSub, setFilterSub] = useState("");
  const [editing, setEditing] = useState(() => !!focusSvc || !services.some((s) => asgs.some((a) => a.service_id === s.id)));
  const [saving, setSaving] = useState(false);
  // Hizmet Ekle'den seçilen alt hizmet: düzenlemeyi aç, satıra kaydır, taşeron kutusuna odaklan
  useEffect(() => {
    if (!focusSvc) return;
    setEditing(true);
    const t = setTimeout(() => {
      document.querySelector(`[data-testid="bs-svc-row-${focusSvc}"]`)?.scrollIntoView({ block: "center", behavior: "smooth" });
      document.querySelector(`[data-testid="bs-sub-${focusSvc}"]`)?.focus();
    }, 150);
    return () => clearTimeout(t);
  }, [focusSvc]);
  const { rows, cardsOf, sorted, setEntry, pickSub, applyToAll: applyRows, addEntry, removeLocal, reset } = useServiceRows({ services, asgs, branchSubIds, filterSub });
  const applyToAll = (s, e) => {
    const n = applyRows(s.id, e.key);
    if (n) toast.success(`${subLabel(e.subcontractor_id)} ve geçerlilik tarihi ${n} alt hizmete uygulandı — Kaydet ile yazılır`);
  };
  const cancel = () => { reset(); setEditing(false); };
  const cancelRef = useRef(cancel);
  cancelRef.current = cancel;
  // Esc → düzenlemeyi iptal et (açık bir taşeron listesi / Hizmet Ekle menüsü yoksa)
  useEffect(() => {
    if (!editing) return;
    const onKey = (e) => { if (e.key === "Escape" && !document.querySelector("[data-searchable][data-testid$=-dropdown], [data-testid=bs-add-menu]")) { e.stopPropagation(); e.stopImmediatePropagation(); cancelRef.current(); } };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [editing]);
  const subLabel = (id) => caris.find((c) => c.id === id)?.name || asgs.find((a) => a.subcontractor_id === id)?.subcontractor_name || id;
  const chipIds = useMemo(() => [...new Set([...branchSubIds, ...asgs.filter((a) => a.subcontractor_id && services.some((s) => s.id === a.service_id)).map((a) => a.subcontractor_id)])], [branchSubIds, asgs, services]);
  const subOpts = useMemo(() => {
    const used = new Set([...asgs.map((a) => a.subcontractor_id).filter(Boolean), ...branchSubIds]);
    // şube kartında taşeron seçilmişse yalnız onlar (ve zaten atanmış olanlar) listelenir
    return caris.filter((c) => branchSubIds.length ? used.has(c.id) : (c.status === "Aktif" || used.has(c.id))).map((c) => ({ value: c.id, label: c.name }));
  }, [caris, asgs, branchSubIds]);
  const addSubOpts = useMemo(() => caris.filter((c) => c.status === "Aktif" && !chipIds.includes(c.id)).map((c) => ({ value: c.id, label: c.name })), [caris, chipIds]);

  const save = async () => {
    setSaving(true);
    try {
      const n = await persistRows({ sorted, rows, cardsOf, locId: loc.id });
      toast.success(n ? `${n} kayıt kaydedildi` : "Değişiklik yok");
      setEditing(false);
      await reload();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  const remove = async (s, e) => {
    if (!e.id) { removeLocal(s.id, e.key); return; }
    if (!window.confirm(`"${s.name}" için bu şubedeki taşeron ataması silinsin mi?`)) return;
    try { await http.delete(`/assignments/${e.id}`); toast.success("Atama silindi"); await reload(); } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="mt-3 border border-slate-200 rounded-md" data-testid={`bs-services-${cat.id}`}>
      <RowsHeader cat={cat} count={services.length} editing={editing} saving={saving} onSave={save} onEdit={() => setEditing(true)} onCancel={cancel} />
      <SubChips cat={cat} chipIds={chipIds} filterSub={filterSub} setFilterSub={setFilterSub} editing={editing} branchSubIds={branchSubIds} saveSubs={saveSubs} subLabel={subLabel} addSubOpts={addSubOpts} />
      <table className="w-full">
        <thead className="bg-slate-50">
          <tr>{TABLE_HEADERS.map((h) => <th key={h || "islem"} title={editing ? HEADER_HINT[h] : undefined} className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-slate-600 font-semibold text-left">{h}{editing && HEADER_HINT[h] && <span className="ml-1 normal-case tracking-normal font-normal text-sky-700" data-testid="bs-apply-hint">(✓✓ tümüne uygula)</span>}</th>)}</tr>
        </thead>
        <ServiceTableBody sorted={sorted} rows={rows} cardsOf={cardsOf} filterSub={filterSub} editing={editing} subOpts={subOpts} pickSub={pickSub} setEntry={setEntry} applyToAll={applyToAll} addEntry={addEntry} remove={remove} focusSvc={focusSvc} />
      </table>
    </div>
  );
}
